import { IBalusterGeometry, IRailingGeometry, IStairCalculationResult, IStairConfig, IStepGeometry, IVec3 } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import { IElevationMap } from './elevation-railing';
import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPlanPrimitive,
    ITextPrimitive,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';
import { IStairPoint, ITreadSides, levelPosts, postStations, treadSides } from './top-plan-marks';

/** A tread or a run seen at a slant flatter than this share of its plan length is not in profile. */
const PROFILE_SHARE: number = 0.8;
/** How far the rise and going dimensions stand off their tread (px). */
const TREAD_DIM_GAP: number = 8;
/** A handrail that turns in plan by more than this starts a new straight leg (radians). */
const LEG_TURN: number = 0.05;
/** A handrail leg shorter than this in plan carries no length: a piece over a winder (mm). */
const MIN_LEG: number = 300;
/** Two equal lengths closer than this are one handrail seen twice (px). */
const TWIN_PX: number = 12;
/** How far a handrail run length stands above the handrail (px). */
const RUN_LABEL_GAP: number = 8;

const NUMBER_FONT: IFont = { weight: 500, size: 9, family: FONT_MONO_FAMILY };
const RUN_FONT: IFont = { weight: 400, size: 8, family: FONT_MONO_FAMILY };

/** A tread's back and front middles in plan: the line its going runs along. */
interface ITreadAxis {
    readonly back: IStairPoint;
    readonly front: IStairPoint;
    readonly centre: IStairPoint;
}

/** A straight leg of a handrail run: its true length along the slope, its ends and middle. */
export interface IHandrailLeg {
    readonly length: number;
    readonly planLength: number;
    readonly from: IVec3;
    readonly to: IVec3;
    readonly mid: IVec3;
}

function middle(a: IStairPoint, b: IStairPoint): IStairPoint {
    return { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
}

function treadAxis(step: IStepGeometry): ITreadAxis {
    const { sides, centre }: ITreadSides = treadSides(step);
    const [first, second]: readonly (readonly IStairPoint[])[] = sides;
    return {
        back: middle(first[0], second[0]),
        front: middle(first[first.length - 1], second[second.length - 1]),
        centre,
    };
}

/** A plan length runs across the view: it shows at nearly full length, seen in profile. */
function inProfile(from: IStairPoint, to: IStairPoint, length: number, map: IElevationMap): boolean {
    return Math.abs(map.across(to) - map.across(from)) >= length * PROFILE_SHARE;
}

function label(at: [number, number], value: string, color: EColorRef, font: IFont, optional: boolean): ITextPrimitive {
    return { kind: EPrimitiveKind.Text, text: value, align: ETextAlign.Center, baseline: ETextBaseline.Middle, at, color, font, optional };
}

function riseText(rise: number): string {
    return String(Math.round(rise * 10) / 10);
}

/** The rise and going of a flight's lowest tread: the going over its top, the rise at its nose. */
function pushTreadDims(
    primitives: IPlanPrimitive[],
    axis: ITreadAxis,
    top: number,
    result: IStairCalculationResult,
    map: IElevationMap,
    measurer: ITextMeasurer
): void {
    const front: number = map.wx(map.across(axis.front));
    const back: number = map.wx(map.across(axis.back));
    const rise: number = result.riserHeight;
    primitives.push(
        horizontalDim(
            Math.min(front, back),
            map.wy(top) - TREAD_DIM_GAP,
            Math.max(front, back),
            String(Math.round(result.treadDepth)),
            measurer
        )
    );
    const side: number = front < back ? front - TREAD_DIM_GAP : front + TREAD_DIM_GAP;
    primitives.push(verticalDim(side, map.wy(top), map.wy(top - rise), riseText(rise), measurer));
}

/**
 * The number of every tread seen in profile, on its body half a rise under its top, as the top
 * view numbers it; and at the lowest tread of every flight in profile, its rise and going. A tread
 * seen face on — its flight runs toward the viewer — is numbered on the other view.
 */
export function pushElevationTreadMarks(
    primitives: IPlanPrimitive[],
    result: IStairCalculationResult,
    map: IElevationMap,
    measurer: ITextMeasurer
): void {
    let previous: IStepGeometry | null = null;
    for (const step of result.geometry.steps) {
        const axis: ITreadAxis = treadAxis(step);
        const going: number = Math.hypot(axis.front.x - axis.back.x, axis.front.z - axis.back.z);
        if (step.isLanding || !inProfile(axis.back, axis.front, going, map)) {
            previous = null;
            continue;
        }
        const top: number = step.position.y;
        const centre: [number, number] = [map.wx(map.across(axis.centre)), map.wy(top - result.riserHeight / 2)];
        primitives.push(label(centre, String(step.index + 1), EColorRef.Ink2, NUMBER_FONT, true));
        const firstOfFlight: boolean = previous?.rotation !== step.rotation || Boolean(previous.winderCorners);
        if (!step.winderCorners && firstOfFlight) {
            pushTreadDims(primitives, axis, top, result, map, measurer);
        }
        previous = step;
    }
}

/** The point of a handrail at a plan distance from its start. */
function handrailAt(points: readonly IVec3[], s: number): IVec3 {
    let start: number = 0;
    for (let i: number = 1; i < points.length; i++) {
        const plan: number = Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
        if (plan > 0 && start + plan >= s) {
            const t: number = Math.max(0, (s - start) / plan);
            return {
                x: points[i - 1].x + (points[i].x - points[i - 1].x) * t,
                y: points[i - 1].y + (points[i].y - points[i - 1].y) * t,
                z: points[i - 1].z + (points[i].z - points[i - 1].z) * t,
            };
        }
        start += plan;
    }
    return points[points.length - 1];
}

/** The handrail between two plan distances: the polyline through every vertex in between. */
function handrailPiece(points: readonly IVec3[], from: number, to: number): IVec3[] {
    const piece: IVec3[] = [handrailAt(points, from)];
    let start: number = 0;
    for (let i: number = 1; i < points.length; i++) {
        start += Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
        if (start > from && start < to) {
            piece.push(points[i]);
        }
    }
    piece.push(handrailAt(points, to));
    return piece;
}

function pathLength(points: readonly IVec3[], withHeight: boolean): number {
    let length: number = 0;
    for (let i: number = 1; i < points.length; i++) {
        length += Math.hypot(points[i].x - points[i - 1].x, withHeight ? points[i].y - points[i - 1].y : 0, points[i].z - points[i - 1].z);
    }
    return length;
}

/** A plan direction of a handrail segment, radians; null for a rise straight up a post. */
function heading(a: IVec3, b: IVec3): number | null {
    return Math.hypot(b.x - a.x, b.z - a.z) > 0 ? Math.atan2(b.z - a.z, b.x - a.x) : null;
}

/** A handrail run cut into straight legs where it turns in plan: over a turn, winders included. */
function straightLegs(points: readonly IVec3[]): IVec3[][] {
    const legs: IVec3[][] = [];
    let leg: IVec3[] = [points[0]];
    let direction: number | null = null;
    for (let i: number = 1; i < points.length; i++) {
        const next: number | null = heading(points[i - 1], points[i]);
        const turn: number =
            next === null || direction === null ? 0 : Math.abs(Math.atan2(Math.sin(next - direction), Math.cos(next - direction)));
        if (turn > LEG_TURN) {
            legs.push(leg);
            leg = [points[i - 1]];
        }
        leg.push(points[i]);
        direction = next ?? direction;
    }
    legs.push(leg);
    return legs;
}

/**
 * Every straight leg of a stair railing's handrail between two neighbouring post faces and the
 * turns between them, measured along its slope: the lengths the shop cuts the handrail to. A leg
 * shorter than a tread — a piece over a winder — carries none.
 */
export function handrailLegs(railing: IRailingGeometry, posts: readonly IBalusterGeometry[]): IHandrailLeg[] {
    const line: IVec3[] = railing.handrail.points;
    const half: number = railing.postWidth / 2;
    const stations: number[] = postStations(railing, posts);
    const legs: IHandrailLeg[] = [];
    for (let i: number = 1; i < stations.length; i++) {
        const from: number = stations[i - 1] + half;
        const to: number = stations[i] - half;
        if (to - from <= railing.postWidth) {
            continue;
        }
        for (const leg of straightLegs(handrailPiece(line, from, to))) {
            const planLength: number = pathLength(leg, false);
            if (planLength >= MIN_LEG) {
                const ends: [IVec3, IVec3] = [leg[0], leg[leg.length - 1]];
                legs.push({
                    length: pathLength(leg, true),
                    from: ends[0],
                    to: ends[1],
                    mid: { x: (ends[0].x + ends[1].x) / 2, y: (ends[0].y + ends[1].y) / 2, z: (ends[0].z + ends[1].z) / 2 },
                    planLength,
                });
            }
        }
    }
    return legs;
}

/**
 * The true length of every straight stair handrail leg seen in profile, written above its middle.
 * A leg seen at a slant is measured on the other view, where it lies across.
 */
export function pushElevationRunLengths(
    primitives: IPlanPrimitive[],
    result: IStairCalculationResult,
    config: IStairConfig,
    map: IElevationMap
): void {
    if (!config.railing.enabled) {
        return;
    }
    const stair: IRailingGeometry[] = result.geometry.railings.filter((railing: IRailingGeometry) => railing.kind !== 'upper');
    const written: ITextPrimitive[] = [];
    for (const railing of stair) {
        for (const leg of handrailLegs(railing, levelPosts(stair, railing))) {
            const at: [number, number] = [map.wx(map.across(leg.mid)), map.wy(leg.mid.y) - RUN_LABEL_GAP];
            const value: string = String(Math.round(leg.length));
            // The two railings of a flight fall on one line in its profile: one length is enough.
            const twin: boolean = written.some(
                (other: ITextPrimitive) => other.text === value && Math.hypot(other.at[0] - at[0], other.at[1] - at[1]) < TWIN_PX
            );
            if (inProfile(leg.from, leg.to, leg.planLength, map) && !twin) {
                const mark: ITextPrimitive = label(at, value, EColorRef.Handrail, RUN_FONT, false);
                written.push(mark);
                primitives.push(mark);
            }
        }
    }
}
